import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaBuilding, 
  FaUser, 
  FaUserTie, 
  FaCrown, 
  FaCog, 
  FaUsers,
  FaChartBar,
  FaFileAlt,
  FaCogs,
  FaShieldAlt
} from 'react-icons/fa';
import DataApi from '../api/DataApi';
import { theme } from '../theme/theme';

const MenuPage = () => {
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, object, custom

  useEffect(() => {
    fetchTabs();
  }, []);

  const fetchTabs = async () => {
    try {
      setLoading(true);
      const tabApi = new DataApi('tabs');
      const response = await tabApi.fetchAll('?is_active=true');
      
      if (response.data.success) {
        setTabs(response.data.data);
      } else {
        setError('Failed to fetch tabs');
      }
    } catch (error) {
      console.error('Error fetching tabs:', error);
      setError('Error loading menu items');
    } finally {
      setLoading(false);
    }
  };

  const getIconForTab = (tab) => {
    const iconMap = {
      'account': <FaBuilding size={24} />,
      'contact': <FaUser size={24} />,
      'lead': <FaUserTie size={24} />,
      'user': <FaCrown size={24} />,
      'employee': <FaUsers size={24} />,
      'custom': <FaCog size={24} />,
      'report': <FaChartBar size={24} />,
      'document': <FaFileAlt size={24} />,
      'settings': <FaCogs size={24} />,
      'permission': <FaShieldAlt size={24} />
    };

    // Try to match by object_name first, then by name
    const key = tab.object_name?.toLowerCase() || tab.name?.toLowerCase();
    return iconMap[key] || iconMap[tab.type] || <FaCog size={24} />;
  };

  const getCardColor = (tab) => {
    if (tab.is_standard) {
      return {
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
        color: theme.colors.white
      };
    }
    
    return {
      background: `linear-gradient(135deg, ${theme.colors.light} 0%, ${theme.colors.white} 100%)`,
      color: theme.colors.text
    };
  };

  const handleTabClick = (tab) => {
    // Add timestamp parameter to force refresh
    const refreshParam = `?refresh=${Date.now()}`;
    
    if (tab.url) {
      // Check if URL already has query parameters
      const separator = tab.url.includes('?') ? '&' : '?';
      navigate(`${tab.url}${separator}refresh=${Date.now()}`);
    } else {
      // Fallback navigation based on object_name
      if (tab.object_name) {
        navigate(`/listview/${tab.object_name.toLowerCase()}${refreshParam}`);
      } else if (tab.type === 'custom') {
        navigate(`/custom/${refreshParam}`);
      }
    }
  };

  const filteredTabs = tabs.filter(tab => {
    if (filter === 'all') return true;
    return tab.type === filter;
  });

  const groupedTabs = filteredTabs.reduce((groups, tab) => {
    const group = tab.type === 'object' ? 'CRM Objects' : 'Custom & Tools';
    if (!groups[group]) groups[group] = [];
    groups[group].push(tab);
    return groups;
  }, {});

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ color: theme.colors.primary }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3" style={{ color: theme.colors.textMuted }}>
            Loading menu items...
          </p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchTabs}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1 style={{ 
            color: theme.colors.text,
            fontSize: theme.typography.h2,
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing.sm
          }}>
            Application Menu
          </h1>
          <p style={{ 
            color: theme.colors.textMuted,
            fontSize: theme.typography.body
          }}>
            Access your CRM objects, tools, and settings
          </p>
        </Col>
      </Row>

      {/* Filter Buttons */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex gap-2">
            <button
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('all')}
            >
              All ({tabs.length})
            </button>
            <button
              className={`btn ${filter === 'object' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('object')}
            >
              Objects ({tabs.filter(t => t.type === 'object').length})
            </button>
            <button
              className={`btn ${filter === 'custom' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('custom')}
            >
              Custom ({tabs.filter(t => t.type === 'custom').length})
            </button>
          </div>
        </Col>
      </Row>

      {/* Menu Items */}
      {Object.entries(groupedTabs).map(([groupName, groupTabs]) => (
        <div key={groupName} className="mb-5">
          <h3 style={{ 
            color: theme.colors.text,
            fontSize: theme.typography.h4,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: theme.spacing.md,
            borderBottom: `2px solid ${theme.colors.border}`,
            paddingBottom: theme.spacing.sm
          }}>
            {groupName}
          </h3>
          
          <Row>
            {groupTabs.map((tab) => {
              const cardStyle = getCardColor(tab);
              return (
                <Col key={tab.id} xs={12} sm={6} md={4} lg={3} className="mb-3">
                  <Card
                    className="h-100 menu-card"
                    style={{
                      cursor: 'pointer',
                      transition: theme.transitions.default,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      boxShadow: theme.shadows.small,
                    }}
                    onClick={() => handleTabClick(tab)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme.shadows.medium;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = theme.shadows.small;
                    }}
                  >
                    <Card.Body 
                      className="text-center p-4"
                      style={cardStyle}
                    >
                      <div className="mb-3">
                        {getIconForTab(tab)}
                      </div>
                      
                      <Card.Title 
                        style={{
                          fontSize: theme.typography.h5,
                          fontWeight: theme.typography.fontWeightMedium,
                          marginBottom: theme.spacing.sm
                        }}
                      >
                        {tab.label}
                      </Card.Title>
                      
                      <Card.Text 
                        style={{
                          fontSize: theme.typography.small,
                          opacity: 0.8,
                          marginBottom: theme.spacing.md
                        }}
                      >
                        {tab.object_name ? `Manage ${tab.label} data` : tab.label}
                      </Card.Text>
                      
                      <div className="d-flex justify-content-center gap-2 flex-wrap">
                        {tab.is_standard && (
                          <Badge 
                            bg="light" 
                            text="dark"
                            style={{ fontSize: '10px' }}
                          >
                            Standard
                          </Badge>
                        )}
                        <Badge 
                          bg={tab.is_active ? 'success' : 'secondary'}
                          style={{ fontSize: '10px' }}
                        >
                          {tab.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge 
                          bg="info"
                          style={{ fontSize: '10px' }}
                        >
                          {tab.type}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      ))}

      {/* Empty State */}
      {filteredTabs.length === 0 && (
        <Row>
          <Col>
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
                📋
              </div>
              <h4 style={{ color: theme.colors.text, marginBottom: theme.spacing.sm }}>
                No menu items found
              </h4>
              <p style={{ color: theme.colors.textMuted }}>
                {filter === 'all' 
                  ? 'No active tabs are available.' 
                  : `No ${filter} tabs are available.`
                }
              </p>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default MenuPage;
